import { ReactNode } from 'react';
import { FaArrowUp, FaArrowDown } from './icons';
import Term from './Term';
import { GLOSSARY, type GlossaryKey } from '../../lib/glossary';

export type StatTone = 'success' | 'warning' | 'danger' | 'accent' | 'muted';

export interface StatItem {
  label: string;
  value: ReactNode;
  tone?: StatTone;
  /** Small line under the value. */
  hint?: ReactNode;
  /** Percent change rendered as an arrow + number next to the value. */
  delta?: number | null;
  /** Glossary key or inline text explained on hover/focus of the label. */
  help?: GlossaryKey | string;
}

interface Props {
  items: StatItem[];
  /** Columns at desktop width; narrower screens fall back to 2 / 3. */
  columns?: 3 | 4 | 6;
  /** Draw the subtle bordered box (for use inside a card). */
  boxed?: boolean;
  /** Exam `.strip`: hairline rules between cells (18px padding beside them) and a 28px value. */
  divided?: boolean;
  /** Value size: `lg` is the Paper `.stat .kv` (30px) instead of 28px (`divided`) / 24px. */
  valueSize?: 'md' | 'lg';
  className?: string;
}

const COLS: Record<NonNullable<Props['columns']>, string> = {
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
  6: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-6',
};

// `divided`: the hairline rule and its 18px padding sit between cells of a row only; the first cell
// of every row (per breakpoint, matching COLS) has neither, like the Exam `.strip` resets.
const DIVIDED: Record<NonNullable<Props['columns']>, string> = {
  3: '[&:nth-child(2n+1)]:border-l-0 [&:nth-child(2n+1)]:pl-0 [&:not(:nth-child(2n+1))]:border-l [&:not(:nth-child(2n+1))]:pl-[18px] sm:[&:nth-child(3n+1)]:border-l-0 sm:[&:nth-child(3n+1)]:pl-0 sm:[&:not(:nth-child(3n+1))]:border-l sm:[&:not(:nth-child(3n+1))]:pl-[18px]',
  4: '[&:nth-child(2n+1)]:border-l-0 [&:nth-child(2n+1)]:pl-0 [&:not(:nth-child(2n+1))]:border-l [&:not(:nth-child(2n+1))]:pl-[18px] md:[&:nth-child(4n+1)]:border-l-0 md:[&:nth-child(4n+1)]:pl-0 md:[&:not(:nth-child(4n+1))]:border-l md:[&:not(:nth-child(4n+1))]:pl-[18px]',
  6: '[&:nth-child(2n+1)]:border-l-0 [&:nth-child(2n+1)]:pl-0 [&:not(:nth-child(2n+1))]:border-l [&:not(:nth-child(2n+1))]:pl-[18px] sm:[&:nth-child(3n+1)]:border-l-0 sm:[&:nth-child(3n+1)]:pl-0 sm:[&:not(:nth-child(3n+1))]:border-l sm:[&:not(:nth-child(3n+1))]:pl-[18px] xl:[&:nth-child(6n+1)]:border-l-0 xl:[&:nth-child(6n+1)]:pl-0 xl:[&:not(:nth-child(6n+1))]:border-l xl:[&:not(:nth-child(6n+1))]:pl-[18px]',
};

const TONE: Record<StatTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  // Design `.lo-t` (accent-ink) and a muted value (Paper sheets "Discarded").
  accent: 'text-accent-ink',
  muted: 'text-muted',
};

/** Headline numbers as label-over-value cells: the one KPI layout the panel uses. */
export default function StatStrip({ items, columns = 4, boxed = false, divided = false, valueSize = 'md', className = '' }: Props) {
  const box = boxed ? 'rounded-[14px] bg-surface-2 p-4' : '';
  return (
    <dl className={`grid ${divided ? 'gap-y-[18px]' : 'gap-x-4 gap-y-3'} ${COLS[columns]} ${box} ${className}`}>
      {items.map((s) => (
        <div key={s.label} className={`min-w-0 ${divided ? `border-hairline py-0.5 pr-[18px] ${DIVIDED[columns]}` : ''}`}>
          <dt className="truncate font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
            {s.help ? <Term {...(s.help in GLOSSARY ? { k: s.help as GlossaryKey } : { def: s.help })}>{s.label}</Term> : s.label}
          </dt>
          <dd className={`mt-1.5 flex items-baseline gap-2 font-mono ${valueSize === 'lg' ? 'text-[30px]' : divided ? 'text-[28px]' : 'text-[24px]'} font-semibold tracking-[-0.03em] ${s.tone ? TONE[s.tone] : 'text-ink'}`}>
            <span>{s.value}</span>
            {s.delta != null && (
              <span className={`flex items-center gap-1 font-mono text-xs font-semibold ${
                s.delta > 0 ? 'text-success' : s.delta < 0 ? 'text-danger' : 'text-muted'}`}>
                {s.delta > 0 ? <FaArrowUp /> : s.delta < 0 ? <FaArrowDown /> : null}
                {Math.abs(Math.round(s.delta * 10) / 10)}%
              </span>
            )}
          </dd>
          {s.hint && <p className="mt-1 text-xs text-muted">{s.hint}</p>}
        </div>
      ))}
    </dl>
  );
}
